import z from "zod";
import type { FormInputSchemaParams } from "@/lib/types/form/form-field";

function extractOptionValue(value: unknown): string | undefined {
	if (typeof value === "string") return value;
	if (value && typeof value === "object" && "value" in value) {
		const option = value as { value?: unknown };
		return typeof option.value === "string" ? option.value : undefined;
	}
	return undefined;
}

export default function selectZodSchema({ required, multiple, options }: FormInputSchemaParams) {
	const allowedValues = (options ?? []).map((option) => option.value).filter((value): value is string => typeof value === "string");
	const allowedSet = new Set(allowedValues);
	const isMultiple = Boolean(multiple);
	const isRequired = Boolean(required);

	return z.any().superRefine((value, ctx) => {
		const values = isMultiple ? (Array.isArray(value) ? value.map(extractOptionValue).filter((v): v is string => !!v) : []) : [];

		if (isMultiple) {
			if (value !== null && value !== undefined && !Array.isArray(value)) {
				ctx.addIssue({ code: "custom", message: "invalidSelectMultiple" });
				return;
			}

			if (isRequired && values.length === 0) {
				ctx.addIssue({ code: "custom", message: "required" });
				return;
			}

			const isValid = values.every((optionValue) => allowedSet.has(optionValue));
			if (!isValid) {
				ctx.addIssue({ code: "custom", message: "invalidSelectMultiple" });
			}
			return;
		}

		const optionValue = extractOptionValue(value);
		if (isRequired && !optionValue) {
			ctx.addIssue({ code: "custom", message: "required" });
			return;
		}
		if (optionValue && !allowedSet.has(optionValue)) {
			ctx.addIssue({ code: "custom", message: "invalidSelect" });
		}
	});
}
